import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { builder } from '@builder.io/react';
import { HydrationBoundary, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AxiosResponse } from 'axios';
import { parse } from 'flatted';
import DevToolsProduction from '@/DevToolsProduction';
import { setAllQueryDefaults } from '@/api/apiConfig';
import queryStaleTimes from '@/api/queryStaleTimes';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { ResourceLocation, RouteResponse, SegmentationId } from '@/generated/data.schemas';
import { getRoute } from '@/generated/routing';
import { App } from './App';
import './src/builder-settings';

interface WindowProps extends Window {
	__REACT_QUERY_STATE__: { state: string } | null;
	__BUILDER_PUBLIC_API_KEY__: string;
	initialData: {
		routing: ResourceLocation | null;
		locale: string;
		isProduction?: boolean;
	};
	ssrError: { msg: string };
	__SEGMENTATION_ID_: SegmentationId;
}

declare const window: WindowProps;
const initialData = window.initialData || { routing: null, locale: 'en' };
let segmentationId = window.__SEGMENTATION_ID_ || 1;
const ssrError = window.ssrError || { msg: null };
const dehydratedState: null | unknown = window.__REACT_QUERY_STATE__ ? parse(window.__REACT_QUERY_STATE__.state) : null;
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: queryStaleTimes.default, // global default to prevent duplicate queries across render cycles
		},
	},
});
setAllQueryDefaults(queryClient);

builder.init(window.__BUILDER_PUBLIC_API_KEY__);

if (initialData.locale) {
	builder.setUserAttributes({ locale: initialData.locale });
}

let routing: undefined | AxiosResponse<RouteResponse, unknown>;

function getRouting(): Promise<void> {
	return getRoute({ path: window.location.pathname }).then((data) => {
		routing = data;
	});
}

function renderPage(): void {
	const container = document.getElementById('react-app');
	if (!container) {
		throw new Error('react-app-element not found!');
	}

	const initialRoute = initialData.routing || routing?.data.route;

	const app = (
		<QueryClientProvider client={queryClient}>
			<HydrationBoundary state={dehydratedState}>
				<SegmentationContext.Provider value={{ segmentationId: segmentationId, url: window.location.origin }}>
					<HelmetProvider>
						<BrowserRouter>
							<Routes>
								<Route path="*" element={<App initialRoute={initialRoute} />} />
							</Routes>
						</BrowserRouter>
					</HelmetProvider>
				</SegmentationContext.Provider>
			</HydrationBoundary>
			<ReactQueryDevtools initialIsOpen={false} buttonPosition={'bottom-left'} />
			<DevToolsProduction />
		</QueryClientProvider>
	);
	if (!dehydratedState) {
		const root = createRoot(container);
		root.render(app);
	} else {
		hydrateRoot(container, app);
	}
}

if (ssrError.msg) {
	console.error('SSR Failed', ssrError.msg);
}

if (!dehydratedState) {
	if (!initialData.isProduction) {
		console.log('in client, NO initial state');
	}

	getRouting().then(() => {
		segmentationId = routing?.data.segmentationId ?? 0;
		renderPage();
	});
} else {
	if (!initialData.isProduction) {
		console.log('in client, HAS initial state');
	}

	renderPage();
}
